/**
 * Rangement automatique du dashboard : regroupe les widgets par type puis comble
 * les trous laissés par le mode `float`.
 *
 * Stratégie « first-fit » : après tri par type (ordre du catalogue, puis position
 * de lecture actuelle), chaque widget est posé au PREMIER emplacement libre balayé
 * de haut en bas et de gauche à droite. Le tri par type reste une préférence (les
 * groupes restent globalement ensemble), mais un widget plus petit peut « reboucher »
 * un trou laissé plus haut par un widget plus large : l'ordre strict par type peut
 * donc être localement rompu, au profit d'un remplissage compact, sans trou.
 *
 * Les déplacements sont appliqués dans un seul `batchUpdate` — même approche que la
 * remise à plat du drag (voir DashboardSave.onDragStop) : la cible étant un layout
 * sans collision, GridStack la conserve telle quelle au commit.
 */
import type { GridStack, GridStackWidget } from "gridstack";
import { WIDGET_CATALOG, WidgetTypeId } from "./widgetCatalog";
import { parseWidgetContent } from "./widgetDraft";

/** Ordre de regroupement des types = ordre d'apparition dans le catalogue. */
const TYPE_ORDER = new Map<string, number>(
  WIDGET_CATALOG.map((entry, index) => [entry.id, index])
);

const orderOf = (type: WidgetTypeId | undefined): number =>
  type !== undefined
    ? (TYPE_ORDER.get(type) ?? Number.MAX_SAFE_INTEGER)
    : Number.MAX_SAFE_INTEGER;

export function autoArrangeGrid(
  grid: GridStack,
  metaMap: Map<string, GridStackWidget>
): void {
  const columns = grid.getColumn() || 1;

  const items = grid.getGridItems().map((el) => {
    const node = el.gridstackNode;
    const id = node?.id != null ? String(node.id) : undefined;
    // Le type est lu depuis le `content` (metaMap = source à jour ; repli sur le node).
    const content = (id && metaMap.get(id)?.content) || node?.content;
    const type = parseWidgetContent(content)?.name as WidgetTypeId | undefined;
    return {
      el,
      x: node?.x ?? 0,
      y: node?.y ?? 0,
      // Un widget plus large que la grille est borné (sinon il ne tiendrait jamais).
      w: Math.min(node?.w ?? 1, columns),
      h: node?.h ?? 1,
      order: orderOf(type),
    };
  });

  if (items.length === 0) return;

  // Tri : type d'abord (préférence de regroupement), puis ordre de lecture courant.
  items.sort((a, b) => a.order - b.order || a.y - b.y || a.x - b.x);

  // Grille d'occupation (lignes de `columns` cellules), agrandie à la demande.
  const occupied: boolean[][] = [];
  const ensureRow = (y: number) => {
    while (occupied.length <= y) {
      occupied.push(new Array<boolean>(columns).fill(false));
    }
  };
  const fits = (x: number, y: number, w: number, h: number): boolean => {
    for (let dy = 0; dy < h; dy++) {
      ensureRow(y + dy);
      for (let dx = 0; dx < w; dx++) {
        if (occupied[y + dy][x + dx]) return false;
      }
    }
    return true;
  };
  const occupy = (x: number, y: number, w: number, h: number) => {
    for (let dy = 0; dy < h; dy++) {
      ensureRow(y + dy);
      for (let dx = 0; dx < w; dx++) occupied[y + dy][x + dx] = true;
    }
  };
  // Premier emplacement libre, balayé de haut en bas puis de gauche à droite.
  // Termine toujours : en descendant, on finit par atteindre des lignes vides.
  const findSlot = (w: number, h: number): { x: number; y: number } => {
    for (let y = 0; ; y++) {
      for (let x = 0; x + w <= columns; x++) {
        if (fits(x, y, w, h)) return { x, y };
      }
    }
  };

  grid.batchUpdate();
  items.forEach((item) => {
    const { x, y } = findSlot(item.w, item.h);
    occupy(x, y, item.w, item.h);
    grid.update(item.el, { x, y });
  });
  grid.batchUpdate(false);
}
